import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useApp } from '../context/AppContext';
import { Clock, Save, Trash2, Plus, Settings, ChevronLeft, ChevronRight, Smartphone, Copy, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const AdminWorkTrackingPage = () => {
  const { getAuthHeaders } = useApp();
  const [entries, setEntries] = useState([]);
  const [pricing, setPricing] = useState({
    weekday_rate: 18.0,
    saturday_rate: 18.5,
    sunday_rate: 20.0,
    currency: 'GBP',
    punch_key: '',
    punch_domain: ''  // Custom domain for mobile link (e.g., rentiasi.ro)
  });
  const [currentWeekStart, setCurrentWeekStart] = useState('');
  const [weekSummary, setWeekSummary] = useState(null);
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Generate mobile punch link based on custom domain or current domain
  const getMobilePunchLink = () => {
    // Use custom domain if set, otherwise use current origin
    const baseUrl = pricing.punch_domain 
      ? (pricing.punch_domain.startsWith('http') ? pricing.punch_domain : `https://${pricing.punch_domain}`)
      : window.location.origin;
    return `${baseUrl}/quick-punch?key=${pricing.punch_key || ''}`;
  };

  const copyPunchLink = () => {
    navigator.clipboard.writeText(getMobilePunchLink());
    setLinkCopied(true);
    toast.success('Link copiat!');
    setTimeout(() => setLinkCopied(false), 3000);
  };

  // Initialize current week
  useEffect(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday is first day
    const monday = new Date(today);
    monday.setDate(today.getDate() - diff);
    setCurrentWeekStart(monday.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (currentWeekStart) {
      fetchPricing();
      fetchWeekData();
    }
  }, [currentWeekStart]);

  const fetchPricing = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/work/pricing`, {
        headers: getAuthHeaders()
      });
      setPricing(data);
    } catch (error) {
      console.error('Failed to fetch pricing');
    }
  };

  const fetchWeekData = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${BACKEND_URL}/api/work/weekly-summary?week_start=${currentWeekStart}`, {
        headers: getAuthHeaders()
      });
      setWeekSummary(data);
      
      // Initialize entries for all days
      const weekEntries = DAYS_OF_WEEK.map((day, index) => {
        const date = new Date(currentWeekStart);
        date.setDate(date.getDate() + index);
        const dateStr = date.toISOString().split('T')[0];
        
        const existing = data.entries.find(e => e.date === dateStr);
        return existing || {
          id: null,
          date: dateStr,
          day_of_week: day,
          entry_type: 'time',
          start_time: '',
          end_time: '',
          hours_worked: 0,
          minutes_worked: 0,
          fixed_amount: 0,
          service_description: '',
          amount: 0,
          notes: ''
        };
      });
      setEntries(weekEntries);
    } catch (error) {
      console.error('Failed to fetch week data');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousWeek = () => {
    const date = new Date(currentWeekStart);
    date.setDate(date.getDate() - 7);
    setCurrentWeekStart(date.toISOString().split('T')[0]);
  };

  const handleNextWeek = () => {
    const date = new Date(currentWeekStart);
    date.setDate(date.getDate() + 7);
    setCurrentWeekStart(date.toISOString().split('T')[0]);
  };

  const handleEntryChange = (index, field, value) => {
    const newEntries = [...entries];
    newEntries[index] = { ...newEntries[index], [field]: value };
    setEntries(newEntries);
  };

  const handleSaveEntry = async (index) => {
    const entry = entries[index];
    
    // Validate based on entry type
    if (entry.entry_type === 'time') {
      if (!entry.start_time || !entry.end_time) {
        toast.error('Please enter both start and end time');
        return;
      }
    } else if (entry.entry_type === 'service_day') {
      if (!entry.fixed_amount || entry.fixed_amount <= 0) {
        toast.error('Please enter a valid amount for Service Day');
        return;
      }
    }
    
    try {
      const payload = {
        date: entry.date,
        day_of_week: entry.day_of_week,
        entry_type: entry.entry_type || 'time',
        start_time: entry.entry_type === 'time' ? entry.start_time : '',
        end_time: entry.entry_type === 'time' ? entry.end_time : '',
        fixed_amount: entry.entry_type === 'service_day' ? parseFloat(entry.fixed_amount) || 0 : 0,
        service_description: entry.entry_type === 'service_day' ? entry.service_description || '' : '',
        notes: entry.notes || ''
      };
      
      if (entry.id) {
        // Update existing
        await axios.put(`${BACKEND_URL}/api/work/entries/${entry.id}`, payload, {
          headers: getAuthHeaders()
        });
        toast.success(`${entry.day_of_week} updated!`);
      } else {
        // Create new
        const { data } = await axios.post(`${BACKEND_URL}/api/work/entries`, payload, {
          headers: getAuthHeaders()
        });
        const newEntries = [...entries];
        newEntries[index] = data;
        setEntries(newEntries);
        toast.success(`${entry.day_of_week} saved!`);
      }
      
      fetchWeekData(); // Refresh to get updated totals
    } catch (error) {
      toast.error('Failed to save entry');
    }
  };

  const handleDeleteEntry = async (index) => {
    const entry = entries[index];
    if (!entry.id) return;
    
    if (!window.confirm('Delete this entry?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/work/entries/${entry.id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Entry deleted');
      fetchWeekData();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleSavePricing = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/work/pricing`, pricing, {
        headers: getAuthHeaders()
      });
      toast.success('Pricing updated!');
      setShowPricingDialog(false);
      fetchWeekData(); // Recalculate amounts
    } catch (error) {
      toast.error('Failed to save pricing');
    }
  };

  const formatCurrency = (amount) => {
    const symbol = pricing.currency === 'EUR' ? '€' : '£';
    return `${symbol}${amount.toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const getWeekRange = () => {
    if (!currentWeekStart) return '';
    const start = new Date(currentWeekStart);
    const end = new Date(currentWeekStart);
    end.setDate(end.getDate() + 6);
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const formatHoursMinutes = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-serif">Work In/Out</h1>
            <p className="text-muted-foreground">Track your working hours</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={async () => {
                if (window.confirm('Acest lucru va șterge intrările duplicate (păstrând doar una per zi). Continuați?')) {
                  try {
                    await axios.post(`${BACKEND_URL}/api/work/cleanup-duplicates`, {}, { headers: getAuthHeaders() });
                    toast.success('Duplicate curățate! Reîncarcă pagina.');
                    fetchWeekData();
                  } catch (error) {
                    toast.error('Eroare la curățare');
                  }
                }
              }} 
              variant="outline"
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
            >
              🧹 Curăță Duplicate
            </Button>
            <Button onClick={() => setShowPricingDialog(true)} variant="outline">
              <Settings size={18} className="mr-2" />
              Pricing Settings
            </Button>
          </div>
        </div>

        {/* Mobile Quick Punch Link */}
        <div className="glass p-4 bg-primary/5 border border-primary/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Smartphone size={24} className="text-primary" />
              <div>
                <p className="font-semibold text-sm">📱 Link Pontaj Mobil</p>
                <p className="text-xs text-muted-foreground truncate max-w-md">
                  {getMobilePunchLink()}
                </p>
              </div>
            </div>
            <Button 
              onClick={copyPunchLink} 
              size="sm" 
              className={linkCopied ? 'bg-green-500' : 'bg-primary'}
            >
              {linkCopied ? <Check size={16} /> : <Copy size={16} />}
              <span className="ml-2">{linkCopied ? 'Copiat!' : 'Copiază'}</span>
            </Button>
          </div>
        </div>

        {/* Week Navigation */}
        <div className="glass p-4 flex items-center justify-between">
          <Button onClick={handlePreviousWeek} variant="ghost" size="sm">
            <ChevronLeft size={20} />
            Previous Week
          </Button>
          <div className="text-center">
            <h2 className="text-xl font-semibold">{getWeekRange()}</h2>
            <p className="text-sm text-muted-foreground">Week starting {currentWeekStart}</p>
          </div>
          <Button onClick={handleNextWeek} variant="ghost" size="sm">
            Next Week
            <ChevronRight size={20} />
          </Button>
        </div>

        {/* Current Rates Display */}
        <div className="grid grid-cols-3 gap-4">
          <div className="glass p-4 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Mon-Fri Rate</p>
            <p className="text-2xl font-semibold text-primary">{formatCurrency(pricing.weekday_rate)}/hr</p>
          </div>
          <div className="glass p-4 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Saturday Rate</p>
            <p className="text-2xl font-semibold text-yellow-500">{formatCurrency(pricing.saturday_rate)}/hr</p>
          </div>
          <div className="glass p-4 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Sunday Rate</p>
            <p className="text-2xl font-semibold text-orange-500">{formatCurrency(pricing.sunday_rate)}/hr</p>
          </div>
        </div>

        {/* Time Entries Table */}
        <div className="glass overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-primary">Day</th>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-primary">Date</th>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-primary">Type</th>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-primary">Time / Amount</th>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-primary">Hours</th>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-primary">Rate</th>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-primary">Total</th>
                <th className="text-left p-4 text-xs uppercase tracking-wider text-primary">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, index) => (
                <tr key={entry.date} className={`border-b border-white/5 ${
                  entry.day_of_week === 'Saturday' ? 'bg-yellow-500/5' :
                  entry.day_of_week === 'Sunday' ? 'bg-orange-500/5' : ''
                }`}>
                  <td className="p-4 font-medium">{entry.day_of_week}</td>
                  <td className="p-4 text-muted-foreground">{formatDate(entry.date)}</td>
                  <td className="p-4">
                    <select
                      value={entry.entry_type || 'time'}
                      onChange={(e) => handleEntryChange(index, 'entry_type', e.target.value)}
                      className="bg-slate-800 border border-white/10 rounded px-2 py-2 text-xs focus:outline-none focus:border-primary"
                    >
                      <option value="time">⏱ Time</option>
                      <option value="service_day">💼 Service Day</option>
                    </select>
                  </td>
                  <td className="p-4">
                    {(entry.entry_type || 'time') === 'time' ? (
                      <div className="flex gap-2 items-center">
                        <input
                          type="time"
                          value={entry.start_time || ''}
                          onChange={(e) => handleEntryChange(index, 'start_time', e.target.value)}
                          className="bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary cursor-pointer"
                        />
                        <span className="text-muted-foreground">→</span>
                        <input
                          type="time"
                          value={entry.end_time || ''}
                          onChange={(e) => handleEntryChange(index, 'end_time', e.target.value)}
                          className="bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary cursor-pointer"
                        />
                      </div>
                    ) : (
                      <div className="flex gap-2 items-center">
                        <span className="text-muted-foreground text-sm">{pricing.currency === 'EUR' ? '€' : '£'}</span>
                        <input
                          type="number"
                          value={entry.fixed_amount || ''}
                          onChange={(e) => handleEntryChange(index, 'fixed_amount', e.target.value)}
                          placeholder="Amount"
                          className="bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-sm w-24 focus:outline-none focus:border-primary"
                          step="0.01"
                        />
                        <input
                          type="text"
                          value={entry.service_description || ''}
                          onChange={(e) => handleEntryChange(index, 'service_description', e.target.value)}
                          placeholder="Description"
                          className="bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-sm w-32 focus:outline-none focus:border-primary"
                        />
                      </div>
                    )}
                  </td>
                  <td className="p-4">
                    {(entry.entry_type || 'time') === 'time' ? (
                      entry.minutes_worked > 0 ? (
                        <span className="text-primary font-medium">
                          {formatHoursMinutes(entry.minutes_worked)}
                        </span>
                      ) : entry.hours_worked > 0 ? (
                        <span className="text-primary font-medium">
                          {entry.hours_worked.toFixed(2)}h
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )
                    ) : (
                      <span className="text-blue-400 text-xs">Service</span>
                    )}
                  </td>
                  <td className="p-4 text-muted-foreground">
                    {(entry.entry_type || 'time') === 'time' && entry.rate > 0 ? formatCurrency(entry.rate) : '-'}
                  </td>
                  <td className="p-4">
                    {entry.amount > 0 ? (
                      <span className="text-green-400 font-semibold">{formatCurrency(entry.amount)}</span>
                    ) : (entry.entry_type === 'service_day' && entry.fixed_amount > 0) ? (
                      <span className="text-blue-400 font-semibold">{formatCurrency(parseFloat(entry.fixed_amount))}</span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleSaveEntry(index)}
                        size="sm"
                        className="bg-primary hover:bg-primary/80"
                      >
                        <Save size={14} />
                      </Button>
                      {entry.id && (
                        <Button
                          onClick={() => handleDeleteEntry(index)}
                          size="sm"
                          variant="destructive"
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Week Summary */}
        {weekSummary && (
          <div className="glass p-6">
            <h3 className="text-lg font-semibold mb-4">Week Summary</h3>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">Total Hours</p>
                <p className="text-3xl font-bold text-primary">
                  {formatHoursMinutes(weekSummary.total_minutes)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Days Worked</p>
                <p className="text-3xl font-bold">{weekSummary.entries.length}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Amount</p>
                <p className="text-3xl font-bold text-green-400">
                  {formatCurrency(weekSummary.total_amount)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Settings Dialog */}
        <Dialog open={showPricingDialog} onOpenChange={setShowPricingDialog}>
          <DialogContent className="bg-slate-950 border-white/10">
            <DialogHeader>
              <DialogTitle>Pricing Settings</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Currency</label>
                <select
                  value={pricing.currency}
                  onChange={(e) => setPricing({ ...pricing, currency: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                >
                  <option value="GBP">£ GBP (British Pound)</option>
                  <option value="EUR">€ EUR (Euro)</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">
                  Monday - Friday Rate (per hour)
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={pricing.weekday_rate}
                  onChange={(e) => setPricing({ ...pricing, weekday_rate: parseFloat(e.target.value) })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">
                  Saturday Rate (per hour)
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={pricing.saturday_rate}
                  onChange={(e) => setPricing({ ...pricing, saturday_rate: parseFloat(e.target.value) })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">
                  Sunday Rate (per hour)
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={pricing.sunday_rate}
                  onChange={(e) => setPricing({ ...pricing, sunday_rate: parseFloat(e.target.value) })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                />
              </div>
              
              {/* Mobile Punch Settings */}
              <div className="border-t border-white/10 pt-4 mt-4">
                <h4 className="font-semibold mb-3">📱 Setări Link Mobil</h4>
                
                <div className="mb-3">
                  <label className="text-sm text-muted-foreground mb-2 block">
                    Domeniu Custom (ex: rentiasi.ro)
                  </label>
                  <input
                    type="text"
                    value={pricing.punch_domain || ''}
                    onChange={(e) => setPricing({ ...pricing, punch_domain: e.target.value })}
                    placeholder="rentiasi.ro"
                    className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Lasă gol pentru a folosi domeniul curent
                  </p>
                </div>
                
                <div className="mb-3">
                  <label className="text-sm text-muted-foreground mb-2 block">
                    Cheie Acces Pontaj
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={pricing.punch_key || ''}
                      readOnly
                      className="flex-1 bg-slate-800/50 border border-white/10 rounded px-3 py-2 text-muted-foreground"
                    />
                    <Button 
                      type="button"
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const newKey = Math.random().toString(36).substring(2, 10);
                        setPricing({ ...pricing, punch_key: newKey });
                      }}
                    >
                      Regenerează
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Această cheie este necesară pentru accesul la pagina de pontaj mobil
                  </p>
                </div>
              </div>
              
              <Button onClick={handleSavePricing} className="w-full bg-primary">
                Save Pricing
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminWorkTrackingPage;
